/**
 * RBAC (Role-Based Access Control) helpers for backoffice
 */

export type StaffRole = "SUPER_ADMIN" | "ADMIN_OPS" | "ADMIN_SALES" | "ADMIN_SUPPORT" | string;

export type PageKey =
  | "DASHBOARD"
  | "COMPANIES"
  | "TENDERS"
  | "GO_QUEUE"
  | "PROJECTS"
  | "PARTNERS"
  | "BILLING"
  | "MESSAGES"
  | "SUPPORT"
  | "PRIVATE_PROJECTS"
  | "SALES_PIPELINE"
  | "SALES_LEADS"
  | "SALES_PERFORMANCE"
  | "SALES_FORECAST"
  | "SALES_ACTIVITIES"
  | "ONBOARDING"
  | "ARTISANS_MODERATION"
  | "TRAVAUXBTP_REFERRALS"
  | "TRAVAUXBTP_DASHBOARD"
  | "CHURN_RISK"
  | "LP_LEADS"
  | "DISPUTES"
  | "CONVERSION_PIPELINE"
  | "TRANSACTIONS"
  | "REVIEWS_MODERATION"
  | "SLA_LEADS"
  | "SUBSCRIPTIONS"
  | "MESSAGE_MODERATION"
  | "AUDIT_LOG"
  | "MARKETPLACE_STATS"
  | "REFUNDS"
  | "ACQUISITION_FUNNEL"
  | "FRAUD_ALERTS"
  | "MARKETPLACE_GEO"
  | "PARTICULIER_QUALITY"
  | "DOCUMENTS_EXPIRATION"
  | "TRIAL_ALERTS"
  | "PLANS_SAAS"
  | "BROADCAST";

export interface NavItem {
  path: string;
  label: string;
  icon?: any;
  badge?: number;
  pageKey?: PageKey;
}

/**
 * Check if a role can access a specific page
 */
export function canAccessPage(role: StaffRole | undefined | null, pageKey: PageKey): boolean {
  if (!role) return false;

  // SUPER_ADMIN can access everything
  if (role === "SUPER_ADMIN") {
    return true;
  }

  // ADMIN_OPS access rules (OPS-only + common pages, NO sales)
  if (role === "ADMIN_OPS") {
    const allowedPages: PageKey[] = [
      "DASHBOARD",
      "COMPANIES",
      "TENDERS",
      "GO_QUEUE",
      "PROJECTS",
      "PARTNERS",
      "MESSAGES",
      "SUPPORT",
      "PRIVATE_PROJECTS",
      "ONBOARDING",
      "ARTISANS_MODERATION",
      "TRAVAUXBTP_REFERRALS",
      "TRAVAUXBTP_DASHBOARD",
      "CHURN_RISK",
      "LP_LEADS",
      "DISPUTES",
      "CONVERSION_PIPELINE",
      "TRANSACTIONS",
      "REVIEWS_MODERATION",
      "SLA_LEADS",
      "BILLING",
      "SUBSCRIPTIONS",
      "MESSAGE_MODERATION",
      "AUDIT_LOG",
      "MARKETPLACE_STATS",
      "REFUNDS",
      "ACQUISITION_FUNNEL",
      "FRAUD_ALERTS",
      "MARKETPLACE_GEO",
      "PARTICULIER_QUALITY",
      "DOCUMENTS_EXPIRATION",
      "TRIAL_ALERTS",
      // Plans SaaS : ADMIN_OPS peut CONSULTER (l'édition est verrouillée
      // côté page + backend au SUPER_ADMIN uniquement).
      "PLANS_SAAS",
      "BROADCAST",
    ];
    return allowedPages.includes(pageKey);
  }

  // ADMIN_SALES access rules (Sales-only + common pages, NO OPS)
  if (role === "ADMIN_SALES") {
    const allowedPages: PageKey[] = [
      "DASHBOARD",
      "COMPANIES",
      "MESSAGES",
      "SUPPORT",
      "PRIVATE_PROJECTS",
      "SALES_PIPELINE",
      "SALES_LEADS",
      "SALES_PERFORMANCE",
      "SALES_FORECAST",
      "SALES_ACTIVITIES",
      "ONBOARDING",
      "BILLING",
      "SUBSCRIPTIONS",
      "AUDIT_LOG",
      "MARKETPLACE_STATS",
      "ACQUISITION_FUNNEL",
    ];
    return allowedPages.includes(pageKey);
  }

  // ADMIN_SUPPORT = universal read-only observer (all business pages, zero write)
  if (role === "ADMIN_SUPPORT") {
    const allowedPages: PageKey[] = [
      "DASHBOARD",
      "COMPANIES",
      "TENDERS",
      "GO_QUEUE",
      "PROJECTS",
      "PARTNERS",
      "MESSAGES",
      "SUPPORT",
      "PRIVATE_PROJECTS",
      "ONBOARDING",
      "ARTISANS_MODERATION",
      "TRAVAUXBTP_REFERRALS",
      "TRAVAUXBTP_DASHBOARD",
      "CHURN_RISK",
      "LP_LEADS",
      "DISPUTES",
      "CONVERSION_PIPELINE",
      "TRANSACTIONS",
      "REVIEWS_MODERATION",
      "SLA_LEADS",
      "BILLING",
      "MESSAGE_MODERATION",
      "SALES_PIPELINE",
      "SALES_LEADS",
      "SALES_PERFORMANCE",
      "SALES_FORECAST",
      "SALES_ACTIVITIES",
      "SUBSCRIPTIONS",
      "AUDIT_LOG",
      "MARKETPLACE_STATS",
      "REFUNDS",
      "ACQUISITION_FUNNEL",
      "DOCUMENTS_EXPIRATION",
    ];
    return allowedPages.includes(pageKey);
  }

  // Unknown role: deny by default
  return false;
}

/**
 * Filter navigation items based on role
 */
export function filterNavItems(role: StaffRole | undefined | null, navItems: NavItem[]): NavItem[] {
  if (!role) return [];

  return navItems.filter((item) => {
    if (!item.pageKey) {
      // If no pageKey, allow for SUPER_ADMIN only (legacy items)
      return role === "SUPER_ADMIN";
    }
    return canAccessPage(role, item.pageKey);
  });
}

/**
 * Check if user can perform write operations (create/edit/delete)
 * SUPER_ADMIN and ADMIN_OPS can write on their allowed pages
 */
export function canWrite(role: StaffRole | undefined | null): boolean {
  return role === "SUPER_ADMIN" || role === "ADMIN_OPS" || role === "ADMIN_SALES";
}

/**
 * Check if user can only read (no write operations)
 * Only ADMIN_SUPPORT is truly read-only
 */
export function isReadOnly(role: StaffRole | undefined | null): boolean {
  return role === "ADMIN_SUPPORT";
}

